import classNames from "classnames";
import { FC, useEffect, useMemo, useState } from "react";

import { ReactComponent as ArrowIcon } from "assets/images/common/arrow.svg";

import Button from "components/Button";

type PropTypes = {
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  className?: string;
  variant?: "primary" | "secondary";
};

const DESKTOP_BREAKPOINT = 800;

function getPages(total: number, page: number, maxVisible: number, edgeCount: number) {
  if (total <= maxVisible + edgeCount * 2) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items: (number | "ellipsis")[] = [];

  // Always show first `edgeCount` pages
  for (let i = 1; i <= edgeCount; i++) {
    items.push(i);
  }

  let start = Math.max(edgeCount + 1, page - Math.floor((maxVisible - 1) / 2));
  let end = Math.min(total - edgeCount, start + maxVisible - 1);
  start = Math.max(edgeCount + 1, end - maxVisible + 1);

  if (start > edgeCount + 1) {
    items.push("ellipsis");
  }

  for (let i = start; i <= end; i++) {
    items.push(i);
  }

  if (end < total - edgeCount) {
    items.push("ellipsis");
  }

  // Always show last `edgeCount` pages
  for (let i = total - edgeCount + 1; i <= total; i++) {
    items.push(i);
  }

  return items;
}

const Pagination: FC<PropTypes> = ({ total, page, onPageChange, className, variant = "primary" }) => {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= DESKTOP_BREAKPOINT);

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const pages = useMemo(() => {
    const maxVisible = isDesktop ? 8 : variant === "secondary" ? 1 : 4;
    const edgeCount = isDesktop ? 2 : 1;
    return getPages(total, page, maxVisible, edgeCount);
  }, [total, page, isDesktop, variant]);

  return (
    <div className={classNames("pagination", `pagination--${variant}`, className)}>
      <Button
        variant={variant === "secondary" ? "secondary" : "link"}
        form={variant === "secondary" ? "circle" : "base"}
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className={classNames("pagination__arrow", "pagination__arrow--left")}
      >
        <ArrowIcon />
      </Button>

      <div className="pagination__pages">
        {pages.map((item, index) =>
          item === "ellipsis" ? (
            <span key={`ellipsis-${index}`} className="pagination__ellipsis">
              ...
            </span>
          ) : (
            <Button
              variant={variant === "secondary" ? "secondary" : page === item ? "primary" : "link"}
              form={variant === "secondary" ? "circle" : "base"}
              className={classNames("pagination__page", { "pagination__page--active": page === item })}
              key={item}
              onClick={() => onPageChange(item)}
            >
              {item}
            </Button>
          ),
        )}
      </div>

      <Button
        variant={variant === "secondary" ? "secondary" : "link"}
        form={variant === "secondary" ? "circle" : "base"}
        disabled={page >= total}
        onClick={() => onPageChange(page + 1)}
        className={classNames("pagination__arrow", "pagination__arrow--right")}
      >
        <ArrowIcon />
      </Button>
    </div>
  );
};

export default Pagination;
